import {
    Check,
    CheckCircle2,
    Eye,
    EyeOff,
    KeyRound,
    LoaderCircle,
    LockKeyhole,
    ShieldCheck,
    X,
    XCircle,
} from "lucide-react";
import {
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
    changePassword,
    clearState,
    verifyCurrentPassword,
} from "../../../../Redux/custmoer_auth/slice";
import { AppDispatch } from "../../../../store";

import "./ChangePasswordPage.css";

type PasswordVisibility = {
    oldPassword: boolean;
    newPassword: boolean;
    confirmPassword: boolean;
    transactionPassword: boolean;
};

type OldPasswordStatus =
    | "idle"
    | "checking"
    | "verified"
    | "invalid"
    | "error";

const SPECIAL_CHARACTER_PATTERN =
    /[!@#$%^&*(),.?":{}|<>_\-+=/\\[\]';`~]/;

const getRequestError = (
    error: unknown,
    fallback: string
) =>
    typeof error === "string"
        ? error
        : error instanceof Error
            ? error.message
            : fallback;

const ChangePasswordPage =
    () => {
        const dispatch =
            useDispatch<AppDispatch>();
        const navigate =
            useNavigate();
        const oldPasswordRef =
            useRef("");

        const [
            oldPassword,
            setOldPassword,
        ] =
            useState("");

        const [
            newPassword,
            setNewPassword,
        ] =
            useState("");

        const [
            confirmPassword,
            setConfirmPassword,
        ] =
            useState("");

        const [
            transactionPassword,
            setTransactionPassword,
        ] =
            useState("");

        const [
            oldPasswordStatus,
            setOldPasswordStatus,
        ] =
            useState<OldPasswordStatus>(
                "idle"
            );

        const [
            oldPasswordMessage,
            setOldPasswordMessage,
        ] =
            useState("");

        const [
            submitting,
            setSubmitting,
        ] =
            useState(false);

        const [
            submitError,
            setSubmitError,
        ] =
            useState("");

        const [
            success,
            setSuccess,
        ] =
            useState(false);

        const [
            visibility,
            setVisibility,
        ] =
            useState<PasswordVisibility>(
                {
                    oldPassword:
                        false,
                    newPassword:
                        false,
                    confirmPassword:
                        false,
                    transactionPassword:
                        false,
                }
            );

        useEffect(
            () => () => {
                dispatch(
                    clearState()
                );
            },
            [dispatch]
        );

        const passwordRules =
            useMemo(
                () => [
                    {
                        id:
                            "length",
                        label:
                            "At least 8 characters",
                        valid:
                            newPassword.length >=
                            8,
                    },
                    {
                        id:
                            "uppercase",
                        label:
                            "At least one uppercase letter",
                        valid:
                            /[A-Z]/.test(
                                newPassword
                            ),
                    },
                    {
                        id:
                            "lowercase",
                        label:
                            "At least one lowercase letter",
                        valid:
                            /[a-z]/.test(
                                newPassword
                            ),
                    },
                    {
                        id:
                            "number",
                        label:
                            "At least one number",
                        valid:
                            /\d/.test(
                                newPassword
                            ),
                    },
                    {
                        id:
                            "special",
                        label:
                            "At least one special character",
                        valid:
                            SPECIAL_CHARACTER_PATTERN.test(
                                newPassword
                            ),
                    },
                ],
                [
                    newPassword,
                ]
            );

        const allPasswordRulesValid =
            passwordRules.every(
                (
                    rule
                ) =>
                    rule.valid
            );

        const confirmStatus =
            !confirmPassword
                ? "idle"
                : newPassword ===
                    confirmPassword
                    ? "matched"
                    : "not-matched";

        const transactionPasswordValid =
            transactionPassword.length >=
            6;

        const canSubmit =
            oldPasswordStatus ===
            "verified" &&
            allPasswordRulesValid &&
            confirmStatus ===
            "matched" &&
            transactionPasswordValid &&
            !submitting;

        const toggleVisibility =
            (
                field: keyof PasswordVisibility
            ) => {
                setVisibility(
                    (
                        previous
                    ) => ({
                        ...previous,
                        [field]:
                            !previous[
                            field
                            ],
                    })
                );
            };

        const handleOldPasswordChange =
            (
                value: string
            ) => {
                oldPasswordRef.current =
                    value;

                setOldPassword(
                    value
                );

                setOldPasswordStatus(
                    "idle"
                );

                setOldPasswordMessage(
                    ""
                );

                setSuccess(
                    false
                );
            };

        const handleVerifyOldPassword =
            async () => {
                if (
                    !oldPassword.trim()
                ) {
                    setOldPasswordStatus(
                        "invalid"
                    );

                    setOldPasswordMessage(
                        "Enter your current password first."
                    );

                    return;
                }

                setOldPasswordStatus(
                    "checking"
                );

                setOldPasswordMessage(
                    ""
                );

                setSubmitError(
                    ""
                );

                const passwordToVerify =
                    oldPassword;

                try {
                    const response =
                        await dispatch(
                            verifyCurrentPassword(
                                {
                                    oldPassword:
                                        passwordToVerify,
                                }
                            )
                        ).unwrap();

                    if (
                        oldPasswordRef.current !==
                        passwordToVerify
                    ) {
                        return;
                    }

                    if (
                        response.verified
                    ) {
                        setOldPasswordStatus(
                            "verified"
                        );

                        setOldPasswordMessage(
                            response.message ||
                            "Current password verified."
                        );

                        return;
                    }

                    setOldPasswordStatus(
                        "invalid"
                    );

                    setOldPasswordMessage(
                        "Old password is incorrect."
                    );
                } catch (
                error
                ) {
                    if (
                        oldPasswordRef.current !==
                        passwordToVerify
                    ) {
                        return;
                    }

                    const message =
                        getRequestError(
                            error,
                            "Unable to verify the password right now."
                        );

                    setOldPasswordStatus(
                        message.toLowerCase().includes(
                            "incorrect"
                        )
                            ? "invalid"
                            : "error"
                    );

                    setOldPasswordMessage(
                        message
                    );
                } finally {
                    dispatch(
                        clearState()
                    );
                }
            };

        const handleSubmit =
            async (
                event: FormEvent<HTMLFormElement>
            ) => {
                event.preventDefault();

                if (
                    !canSubmit
                ) {
                    return;
                }

                setSubmitting(
                    true
                );

                setSubmitError(
                    ""
                );

                setSuccess(
                    false
                );

                try {
                    const response =
                        await dispatch(
                            changePassword(
                                {
                                    oldPassword,
                                    newPassword,
                                    transactionPassword,
                                }
                            )
                        ).unwrap();

                    setSuccess(
                        true
                    );

                    oldPasswordRef.current =
                        "";

                    setOldPassword(
                        ""
                    );

                    setNewPassword(
                        ""
                    );

                    setConfirmPassword(
                        ""
                    );

                    setTransactionPassword(
                        ""
                    );

                    setOldPasswordStatus(
                        "idle"
                    );

                    setOldPasswordMessage(
                        ""
                    );

                    toast.success(
                        response.message ||
                        "Password updated successfully."
                    );

                    navigate(
                        "/Dashboard"
                    );
                } catch (
                error
                ) {
                    setSubmitError(
                        getRequestError(
                            error,
                            "Unable to update the password."
                        )
                    );
                } finally {
                    setSubmitting(
                        false
                    );

                    dispatch(
                        clearState()
                    );
                }
            };

        return (
            <section className="change-password-page">
                <div className="change-password-shell">
                    <div className="change-password-heading">
                        <span className="change-password-kicker">
                            MY ACCOUNT
                        </span>

                        <h1>
                            Change Password
                        </h1>

                        <p>
                            Verify your current password, create a strong new password and confirm the change securely.
                        </p>
                    </div>

                    {success && (
                        <div className="change-password-message success">
                            <CheckCircle2
                                size={
                                    17
                                }
                                strokeWidth={
                                    2
                                }
                            />

                            <span>
                                Password updated successfully.
                            </span>
                        </div>
                    )}

                    {submitError && (
                        <div className="change-password-message error">
                            <XCircle
                                size={
                                    17
                                }
                                strokeWidth={
                                    2
                                }
                            />

                            <span>
                                {
                                    submitError
                                }
                            </span>
                        </div>
                    )}

                    <form
                        className="change-password-form"
                        onSubmit={
                            handleSubmit
                        }
                    >
                        <div className="change-password-block">
                            <div className="change-password-block-head">
                                <span className="change-password-block-icon">
                                    <ShieldCheck
                                        size={
                                            18
                                        }
                                        strokeWidth={
                                            1.9
                                        }
                                    />
                                </span>

                                <div>
                                    <strong>
                                        Verify current password
                                    </strong>

                                    <small>
                                        Confirm your existing login password before continuing.
                                    </small>
                                </div>
                            </div>

                            <div className="change-password-field">
                                <label htmlFor="old-password">
                                    Old Password
                                </label>

                                <div className="change-password-input-wrap">
                                    <LockKeyhole
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            1.8
                                        }
                                    />

                                    <input
                                        id="old-password"
                                        type={
                                            visibility.oldPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={
                                            oldPassword
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleOldPasswordChange(
                                                event.target.value
                                            )
                                        }
                                        placeholder="Enter old password"
                                        autoComplete="current-password"
                                    />

                                    <button
                                        type="button"
                                        className="change-password-eye"
                                        onClick={() =>
                                            toggleVisibility(
                                                "oldPassword"
                                            )
                                        }
                                        aria-label={
                                            visibility.oldPassword
                                                ? "Hide old password"
                                                : "Show old password"
                                        }
                                    >
                                        {visibility.oldPassword ? (
                                            <EyeOff
                                                size={
                                                    17
                                                }
                                            />
                                        ) : (
                                            <Eye
                                                size={
                                                    17
                                                }
                                            />
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        className={`change-password-verify-button ${oldPasswordStatus ===
                                            "verified"
                                            ? "verified"
                                            : ""
                                            }`}
                                        onClick={
                                            handleVerifyOldPassword
                                        }
                                        disabled={
                                            oldPasswordStatus ===
                                            "checking" ||
                                            !oldPassword.trim()
                                        }
                                    >
                                        {oldPasswordStatus ===
                                            "checking" ? (
                                            <>
                                                <LoaderCircle
                                                    size={
                                                        14
                                                    }
                                                    className="spin"
                                                />

                                                Checking
                                            </>
                                        ) : oldPasswordStatus ===
                                            "verified" ? (
                                            <>
                                                <Check
                                                    size={
                                                        14
                                                    }
                                                />

                                                Verified
                                            </>
                                        ) : (
                                            "Verify"
                                        )}
                                    </button>
                                </div>

                                {oldPasswordMessage && (
                                    <div
                                        className={`change-password-field-status ${oldPasswordStatus}`}
                                    >
                                        {oldPasswordStatus ===
                                            "verified" ? (
                                            <CheckCircle2
                                                size={
                                                    14
                                                }
                                            />
                                        ) : (
                                            <XCircle
                                                size={
                                                    14
                                                }
                                            />
                                        )}

                                        <span>
                                            {
                                                oldPasswordMessage
                                            }
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="change-password-block">
                            <div className="change-password-block-head">
                                <span className="change-password-block-icon">
                                    <KeyRound
                                        size={
                                            18
                                        }
                                        strokeWidth={
                                            1.9
                                        }
                                    />
                                </span>

                                <div>
                                    <strong>
                                        Create new password
                                    </strong>

                                    <small>
                                        Your new password must satisfy all security rules below.
                                    </small>
                                </div>
                            </div>

                            <div className="change-password-field">
                                <label htmlFor="new-password">
                                    New Password
                                </label>

                                <div className="change-password-input-wrap">
                                    <LockKeyhole
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            1.8
                                        }
                                    />

                                    <input
                                        id="new-password"
                                        type={
                                            visibility.newPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={
                                            newPassword
                                        }
                                        onChange={(
                                            event
                                        ) => {
                                            setNewPassword(
                                                event.target.value
                                            );

                                            setSuccess(
                                                false
                                            );
                                        }}
                                        placeholder="Enter new password"
                                        autoComplete="new-password"
                                    />

                                    <button
                                        type="button"
                                        className="change-password-eye"
                                        onClick={() =>
                                            toggleVisibility(
                                                "newPassword"
                                            )
                                        }
                                        aria-label={
                                            visibility.newPassword
                                                ? "Hide new password"
                                                : "Show new password"
                                        }
                                    >
                                        {visibility.newPassword ? (
                                            <EyeOff
                                                size={
                                                    17
                                                }
                                            />
                                        ) : (
                                            <Eye
                                                size={
                                                    17
                                                }
                                            />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="change-password-rules">
                                {passwordRules.map(
                                    (
                                        rule
                                    ) => (
                                        <div
                                            className={`change-password-rule ${rule.valid
                                                ? "valid"
                                                : "invalid"
                                                }`}
                                            key={
                                                rule.id
                                            }
                                        >
                                            <span className="change-password-rule-icon">
                                                {rule.valid ? (
                                                    <Check
                                                        size={
                                                            12
                                                        }
                                                        strokeWidth={
                                                            3
                                                        }
                                                    />
                                                ) : (
                                                    <X
                                                        size={
                                                            12
                                                        }
                                                        strokeWidth={
                                                            2.4
                                                        }
                                                    />
                                                )}
                                            </span>

                                            <span>
                                                {
                                                    rule.label
                                                }
                                            </span>
                                        </div>
                                    )
                                )}
                            </div>

                            <div className="change-password-field">
                                <label htmlFor="confirm-password">
                                    Re-enter New Password
                                </label>

                                <div className="change-password-input-wrap">
                                    <LockKeyhole
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            1.8
                                        }
                                    />

                                    <input
                                        id="confirm-password"
                                        type={
                                            visibility.confirmPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={
                                            confirmPassword
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setConfirmPassword(
                                                event.target.value
                                            )
                                        }
                                        placeholder="Re-enter your new password"
                                        autoComplete="new-password"
                                    />

                                    <button
                                        type="button"
                                        className="change-password-eye"
                                        onClick={() =>
                                            toggleVisibility(
                                                "confirmPassword"
                                            )
                                        }
                                        aria-label={
                                            visibility.confirmPassword
                                                ? "Hide confirm password"
                                                : "Show confirm password"
                                        }
                                    >
                                        {visibility.confirmPassword ? (
                                            <EyeOff
                                                size={
                                                    17
                                                }
                                            />
                                        ) : (
                                            <Eye
                                                size={
                                                    17
                                                }
                                            />
                                        )}
                                    </button>
                                </div>

                                {confirmStatus !==
                                    "idle" && (
                                        <div
                                            className={`change-password-match-status ${confirmStatus}`}
                                        >
                                            {confirmStatus ===
                                                "matched" ? (
                                                <CheckCircle2
                                                    size={
                                                        14
                                                    }
                                                />
                                            ) : (
                                                <XCircle
                                                    size={
                                                        14
                                                    }
                                                />
                                            )}

                                            <span>
                                                {confirmStatus ===
                                                    "matched"
                                                    ? "Password matched"
                                                    : "Password not matched"}
                                            </span>
                                        </div>
                                    )}
                            </div>
                        </div>

                        <div className="change-password-block">
                            <div className="change-password-block-head">
                                <span className="change-password-block-icon">
                                    <LockKeyhole
                                        size={
                                            18
                                        }
                                        strokeWidth={
                                            1.9
                                        }
                                    />
                                </span>

                                <div>
                                    <strong>
                                        Transaction confirmation
                                    </strong>

                                    <small>
                                        Enter your transaction password to authorize this security change.
                                    </small>
                                </div>
                            </div>

                            <div className="change-password-field">
                                <label htmlFor="transaction-password">
                                    Transaction Password
                                </label>

                                <div className="change-password-input-wrap">
                                    <LockKeyhole
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            1.8
                                        }
                                    />

                                    <input
                                        id="transaction-password"
                                        type={
                                            visibility.transactionPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={
                                            transactionPassword
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setTransactionPassword(
                                                event.target.value
                                            )
                                        }
                                        placeholder="Enter transaction password"
                                        autoComplete="off"
                                    />

                                    <button
                                        type="button"
                                        className="change-password-eye"
                                        onClick={() =>
                                            toggleVisibility(
                                                "transactionPassword"
                                            )
                                        }
                                        aria-label={
                                            visibility.transactionPassword
                                                ? "Hide transaction password"
                                                : "Show transaction password"
                                        }
                                    >
                                        {visibility.transactionPassword ? (
                                            <EyeOff
                                                size={
                                                    17
                                                }
                                            />
                                        ) : (
                                            <Eye
                                                size={
                                                    17
                                                }
                                            />
                                        )}
                                    </button>
                                </div>

                                {transactionPassword &&
                                    !transactionPasswordValid && (
                                        <div className="change-password-field-status invalid">
                                            <XCircle
                                                size={
                                                    14
                                                }
                                            />

                                            <span>
                                                Transaction password must contain at least 6 characters.
                                            </span>
                                        </div>
                                    )}
                            </div>
                        </div>

                        <div className="change-password-submit-area">
                            <div className="change-password-submit-note">
                                <ShieldCheck
                                    size={
                                        15
                                    }
                                    strokeWidth={
                                        2
                                    }
                                />

                                <span>
                                    For your security, all requirements must be completed before updating.
                                </span>
                            </div>

                            <button
                                type="submit"
                                className="change-password-submit-button"
                                disabled={
                                    !canSubmit
                                }
                            >
                                {submitting ? (
                                    <>
                                        <LoaderCircle
                                            size={
                                                16
                                            }
                                            className="spin"
                                        />

                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <KeyRound
                                            size={
                                                16
                                            }
                                            strokeWidth={
                                                2
                                            }
                                        />

                                        Update Password
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        );
    };

export default ChangePasswordPage;
